import { Link, useParams } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Pill } from '@/features/feeds/components/FeedsNav'
import { DayBars, ErrorNote, GrowthFrame, Kpi, ShareBar, StatusPill, metricCells, money, num, pct, ratio, useSelection } from '../components/GrowthFrame'
import { useCampaign, useProducts, useSearchTerms } from '../hooks/useGrowth'
import { ProductCell } from './ProductsPage'

const LABEL: Record<string, string> = {
  custom_label_0: 'Kind',
  custom_label_1: 'Room',
  custom_label_2: 'Room',
  custom_label_3: 'Sales tier',
  custom_label_4: 'Label 4',
}

export function CampaignPage() {
  const { campaignId } = useParams()
  const sel = useSelection()
  const q = useCampaign(sel.account?.customerId, campaignId, sel.days)
  const terms = useSearchTerms(sel.account?.customerId, { days: sel.days, campaign: campaignId, limit: 10 })
  const products = useProducts(sel.account?.customerId, { days: sel.days, campaign: campaignId, limit: 10 })
  const d = q.data
  const c = d?.campaign
  const cur = d?.account.currency || sel.account?.currency || 'USD'
  // A tree whose catch-all takes nearly everything is not splitting the feed at all — usually a
  // label the groups target never reached Google Ads (Strategy → Feed labels).
  const live = (d?.productGroups || []).filter((g) => !g.excluded)
  const impr = live.reduce((t, g) => t + g.metrics.impressions, 0)
  const catchAll = live.find((g) => g.value == null && g.dimension)
  const named = live.filter((g) => g.value != null)
  const fallThrough = catchAll && named.length > 1 && impr >= 100 && catchAll.metrics.impressions / impr > 0.8 ? catchAll.metrics.impressions / impr : null

  return (
    <GrowthFrame
      title={c?.name || 'Campaign'}
      description={
        <Link to={`/app/growth${sel.qs()}`} className="inline-flex items-center gap-1 hover:underline">
          <ChevronLeft className="h-3 w-3" /> Command center
        </Link>
      }
    >
      {q.error && <ErrorNote error={q.error} />}
      {!c ? (
        !q.error && <Skeleton className="h-64" />
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <StatusPill status={c.status} /> {c.channel.toLowerCase().replace('_', ' ')} · {c.bidding.toLowerCase().replace(/_/g, ' ')} ·
            {money(c.budget, cur, 0)}/day · today so far {money(c.today.cost, cur)}, {num(c.today.clicks)} clicks
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            <Kpi label="Spend" value={money(c.cur.cost, cur)} cur={c.cur.cost} prev={c.prev.cost} good="none"
              hint={<span>{pct(c.cur.cost / (c.budget * (d!.window.days) || 1), 0)} of budget</span>} />
            <Kpi label="Clicks" value={num(c.cur.clicks)} cur={c.cur.clicks} prev={c.prev.clicks} hint={<span>CTR {pct(c.cur.ctr, 2)}</span>} />
            <Kpi label="Avg. CPC" value={c.cur.clicks ? money(c.cur.cpc, cur) : '—'} cur={c.cur.cpc} prev={c.prev.cpc} good="down" />
            <Kpi label="Purchases" value={num(c.cur.purchases)} cur={c.cur.purchases} prev={c.prev.purchases}
              hint={<span>{money(c.cur.value, cur)} value · ROAS {c.cur.cost ? ratio(c.cur.roas) : '—'}</span>} />
            <Card>
              <CardHeader className="pb-1"><CardDescription className="text-xs uppercase tracking-wide">Impression share</CardDescription></CardHeader>
              <CardContent><ShareBar won={c.share.impressionShare} rank={c.share.lostToRank} budget={c.share.lostToBudget} /></CardContent>
            </Card>
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            <DayBars title="Spend" rows={c.series} value={(r) => r.cost} format={(v) => money(v, cur)} />
            <DayBars title="Clicks" rows={c.series} value={(r) => r.clicks} format={(v) => num(v)} />
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Product groups and bids</CardTitle>
              <CardDescription>
                How the campaign splits the feed and what it bids on each split ({d!.adGroups.map((g) => `${g.name}${g.bid ? `, ad group bid ${money(g.bid, cur)}` : ''}`).join(' · ')}).
                "Everything else" catches every product the named groups do not. Bids are changed in Google Ads, not here.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 overflow-x-auto">
              {fallThrough != null && (
                <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                  ⚠ {pct(fallThrough, 0)} of impressions fell through to "Everything else" while {named.length} named groups got{' '}
                  {named.every((g) => !g.metrics.impressions) ? 'none' : 'almost none'}. The split is not working: check that Google Ads receives the label these
                  groups target (<Link className="underline" to={`/app/growth/strategy${sel.qs()}`}>Strategy → Feed labels</Link>).
                </div>
              )}
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Group</TableHead>
                    <TableHead className="text-right">Max CPC</TableHead>
                    <TableHead className="text-right">Impr.</TableHead>
                    <TableHead className="text-right">Clicks</TableHead>
                    <TableHead className="text-right">CPC</TableHead>
                    <TableHead className="text-right">Spend</TableHead>
                    <TableHead className="text-right">Purchases</TableHead>
                    <TableHead className="text-right">ROAS</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {d!.productGroups.map((g) => {
                    const m = metricCells(g.metrics, cur)
                    return (
                      <TableRow key={`${g.adGroupId}:${g.criterionId}`} className={g.excluded ? 'text-muted-foreground' : ''}>
                        <TableCell>
                          <span className="text-xs text-muted-foreground">{g.dimension ? LABEL[g.dimension] || g.dimension : 'All products'}: </span>
                          <span className="font-medium">{g.value || 'Everything else'}</span>
                          {g.excluded && <> <Pill tone="slate">excluded</Pill></>}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{g.bid != null ? money(g.bid, cur) : '—'}</TableCell>
                        <TableCell className="text-right tabular-nums">{m.impressions}</TableCell>
                        <TableCell className="text-right tabular-nums">{m.clicks}</TableCell>
                        <TableCell className="text-right tabular-nums">{m.cpc}</TableCell>
                        <TableCell className="text-right tabular-nums">{m.cost}</TableCell>
                        <TableCell className="text-right tabular-nums">{m.purchases}</TableCell>
                        <TableCell className="text-right tabular-nums">{m.roas}</TableCell>
                      </TableRow>
                    )
                  })}
                  {!d!.productGroups.length && (
                    <TableRow><TableCell colSpan={8} className="text-center text-sm text-muted-foreground">No product groups (not a Shopping campaign, or it targets all products).</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}

      <div className="grid gap-4 xl:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Top search terms</CardTitle>
            <CardDescription>
              From the nightly sync. <Link className="underline" to={`/app/growth/search-terms${sel.qs({ campaign: campaignId || '' })}`}>All terms for this campaign →</Link>
            </CardDescription>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            {terms.error ? <ErrorNote error={terms.error} /> : (
              <Table>
                <TableHeader><TableRow><TableHead>Term</TableHead><TableHead className="text-right">Clicks</TableHead><TableHead className="text-right">Spend</TableHead><TableHead className="text-right">Purch.</TableHead></TableRow></TableHeader>
                <TableBody>
                  {(terms.data?.data || []).map((t) => (
                    <TableRow key={t.term}>
                      <TableCell><span className="font-medium">{t.term}</span> <span className="text-[11px] text-muted-foreground">{t.class.replace('_', ' ')}</span></TableCell>
                      <TableCell className="text-right tabular-nums">{num(t.clicks)}</TableCell>
                      <TableCell className="text-right tabular-nums">{money(t.cost, cur)}</TableCell>
                      <TableCell className="text-right tabular-nums">{num(t.purchases)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Top products</CardTitle>
            <CardDescription>
              From the nightly sync. <Link className="underline" to={`/app/growth/products${sel.qs({ campaign: campaignId || '' })}`}>All products for this campaign →</Link>
            </CardDescription>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            {products.error ? <ErrorNote error={products.error} /> : (
              <Table>
                <TableHeader><TableRow><TableHead>Product</TableHead><TableHead className="text-right">Clicks</TableHead><TableHead className="text-right">Spend</TableHead><TableHead className="text-right">Purch.</TableHead></TableRow></TableHeader>
                <TableBody>
                  {(products.data?.data || []).map((p) => (
                    <TableRow key={p.item_id}>
                      <TableCell><ProductCell p={p} compact /></TableCell>
                      <TableCell className="text-right tabular-nums">{num(p.clicks)}</TableCell>
                      <TableCell className="text-right tabular-nums">{money(p.cost, cur)}</TableCell>
                      <TableCell className="text-right tabular-nums">{num(p.purchases)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </GrowthFrame>
  )
}
